import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { kitStyles, defaultColors, renderKitPreview, KitStyle } from '../utils/kitUtils';

const KitDesigner: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is part of the registration flow
  const isRegistrationFlow = location.state?.fromRegistration === true;
  const [selectedStyle, setSelectedStyle] = useState<string>(user?.kitStyle || '');
  const [primaryColor, setPrimaryColor] = useState<string>(user?.kitPrimaryColour || '#2563eb');
  const [secondaryColor, setSecondaryColor] = useState<string>(user?.kitSecondaryColour || '#ffffff');
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Load existing kit preferences
    if (user.kitStyle) {
      setSelectedStyle(user.kitStyle);
    }
    if (user.kitPrimaryColour) {
      setPrimaryColor(user.kitPrimaryColour);
    }
    if (user.kitSecondaryColour) {
      setSecondaryColor(user.kitSecondaryColour);
    }
  }, [user, navigate]);

  // Also load preferences when component mounts
  useEffect(() => {
    if (user) {
      if (user.kitStyle) {
        setSelectedStyle(user.kitStyle);
      }
      if (user.kitPrimaryColour) {
        setPrimaryColor(user.kitPrimaryColour);
      }
      if (user.kitSecondaryColour) {
        setSecondaryColor(user.kitSecondaryColour);
      }
    }
  }, []);

  const handleSave = async () => {
    if (!selectedStyle) {
      setErrorMessage('Please select a kit style');
      setShowErrorModal(true);
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.put('/users/profile', {
        kitStyle: selectedStyle,
        kitPrimaryColour: primaryColor,
        kitSecondaryColour: secondaryColor
      });

      // Update the user context with the full user data
      if (updateUser) {
        updateUser(response.data);
      }

      // Also refresh the user data from the server to ensure consistency
      try {
        const userResponse = await apiClient.get('/auth/me');
        if (updateUser) {
          updateUser(userResponse.data.user);
        }
      } catch (refreshError) {
        console.error('Failed to refresh user data:', refreshError);
      }

      setShowSuccessModal(true);
      // Auto-navigate after 2 seconds based on flow
      setTimeout(() => {
        if (isRegistrationFlow) {
          navigate('/select-favourite-team', { state: { fromRegistration: true } });
        } else {
          navigate('/profile');
        }
      }, 2000);
    } catch (error: any) {
      console.error('Error saving kit design:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to save kit design');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };



  if (!user) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          {isRegistrationFlow ? 'Design Your Team Kit' : 'Design Your Team Kit'}
        </h1>
        <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
          {isRegistrationFlow 
            ? 'Choose your kit style and colours to personalise your team\'s appearance. This will be your team\'s unique look across the platform.'
            : 'Choose your kit style and colours to personalise your team\'s appearance'
          }
        </p>
      </div>


            {/* Actions */}
       <div className="flex justify-end items-right mb-4">

        <button
          onClick={handleSave}
          disabled={!selectedStyle || saving}
          className="btn-primary"
        >
          {saving 
            ? 'Saving...' 
            : isRegistrationFlow 
              ? 'Save Kit and Continue' 
              : 'Save Kit Design'
          }
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Kit Style Selection */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Choose Kit Style
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {kitStyles.map((style) => (
              <div
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={`cursor-pointer flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                  selectedStyle === style.id
                    ? 'border-green-500 bg-blue-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {renderKitPreview(style, primaryColor, secondaryColor, 'large')}
                <div className="mt-3 text-center">
                  <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                    {style.name}
                  </h3>
                  <p className="text-xs opacity-70" style={{ color: 'var(--text-secondary)' }}>
                    {style.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Color Selection */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Choose Colors
          </h2>
          
          <div className="space-y-8">
            {/* Primary Color */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Primary Color
              </label>
              <div className="flex flex-col items-left">
                <div className="mb-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-12 rounded border-2 border-gray-300 dark:border-gray-600 cursor-pointer"
                />
                </div>
                <div className="items-left justify-left align-left">
                  <div className="flex flex-wrap gap-2">
                    {defaultColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setPrimaryColor(color)}
                        className="w-8 h-8 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Color */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Secondary Color
              </label>
              <div className="flex flex-col items-left">
              <div className="mb-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-12 h-12 rounded border-2 border-gray-300 dark:border-gray-600 cursor-pointer"
                />
                </div>
                <div>
                  <div className="flex flex-wrap gap-2">
                    {defaultColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSecondaryColor(color)}
                        className="w-8 h-8 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      {selectedStyle && (
        <div className="card p-6 mt-8">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Your Kit Preview
          </h2>
          <div className="flex justify-center">
            {renderKitPreview(kitStyles.find(s => s.id === selectedStyle)!, primaryColor, secondaryColor, 'large')}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={() => {
            if (isRegistrationFlow) {
              navigate('/select-favourite-team', { state: { fromRegistration: true } });
            } else {
              navigate('/profile');
            }
          }}
          className="btn-secondary"
        >
          {isRegistrationFlow ? 'Skip' : 'Cancel'}
        </button>
        <button
          onClick={handleSave}
          disabled={!selectedStyle || saving}
          className="btn-primary"
        >
          {saving 
            ? 'Saving...' 
            : isRegistrationFlow 
              ? 'Save Kit and Continue' 
              : 'Save Kit Design'
          }
        </button>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-center mb-2" style={{ color: 'var(--text-primary)' }}>
              Kit Design Saved!
            </h3>
            <p className="text-center mb-4" style={{ color: 'var(--text-secondary)' }}>
              {isRegistrationFlow 
                ? 'Your team kit has been successfully saved. Redirecting to favourite team selection...'
                : 'Your team kit has been successfully saved. Redirecting to profile...'
              }
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  if (isRegistrationFlow) {
                    navigate('/select-favourite-team', { state: { fromRegistration: true } });
                  } else {
                    navigate('/profile');
                  }
                }}
                className="btn-primary"
              >
                {isRegistrationFlow ? 'Continue to Favourite Team' : 'Go to Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-center mb-2" style={{ color: 'var(--text-primary)' }}>
              Error
            </h3>
            <p className="text-center mb-4" style={{ color: 'var(--text-secondary)' }}>
              {errorMessage}
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setShowErrorModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KitDesigner; 